import { Component, ElementRef, Injector, OnInit, QueryList, ViewChildren, afterNextRender, computed, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { ApiSurveyDto, ApiSurveyModelService } from '../../../../shared/services/api-surveymodel.service';
import { ApiErrorResponse } from '../../../../shared/models/api-error-response.interface';
import { ApiCmdMappingModel, ApiSurveyCmdMappingErrorCode, ApiSurveyCmdMappingService, CmdMappingRequestModel } from '../../../../shared/services/api-survey-cmdmapping.service';
import { CmdModel } from '../../models/cmd.model';
import { JsonPipe } from '@angular/common';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { SurveyHeaderComponent } from "../../components/survey-header/survey-header.component";
import { CanDeactivateComponent } from '../survey-builder/survey-builder.guard';
import { InfoTooltipComponent } from '../../../../shared/components/ui/info-tooltip/info-tooltip.component';

type SurveyCMDTab = 'csv' | 'json';
type RowChangeStatus = 'new' | 'modified' | 'deleted' | 'clean';

// A CMD record is identified by the (csvPosition, dbColumn) pair, not by dbColumn alone.
interface CmdKey {
  csvPosition: number | null;
  dbColumn: string;
}

function cmdKeyString(key: CmdKey): string {
  return `${key.csvPosition ?? ''}|${key.dbColumn.trim()}`;
}

interface EditableCmdRow {
  model: CmdModel;
  // The key the record was loaded with — null for a row that has never been saved.
  originalKey: CmdKey | null;
  markedForDelete: boolean;
  translationText: string;
  translationError: string | null;
}

function defaultCmdModel(): CmdModel {
  return {
    csvPosition: null,
    dbColumn: '',
    additionalData: false,
    isMandatory: false,
    isHashColumn: false,
    calculatedColumnRule: null,
    defaultColumnValue: null,
    format: null,
    optionalFormat: null,
    outFormat: null,
    useCustomerLanguagePreference: false,
    isPhoneContact: false,
    ignoreHash: false,
    columnValueTranslation: null
  };
}

@Component({
  selector: 'app-survey-cmd',
  templateUrl: './survey-cmd.component.html',
  styleUrls: ['./survey-cmd.component.css'],
  imports: [JsonPipe, FormsModule, BreadcrumbComponent, PageLoaderComponent, SurveyHeaderComponent, InfoTooltipComponent]
})
export class SurveyCmdComponent implements OnInit, CanDeactivateComponent {
  survey: ApiSurveyDto | null = null;
  loading = signal(false);
  error = signal<string | null>(null);
  activeTab = signal<SurveyCMDTab>('csv');
  rows: EditableCmdRow[] = [];

  @ViewChildren('dbColumnInput') private dbColumnInputs!: QueryList<ElementRef<HTMLInputElement>>;

  readonly canEdit = computed(() => this.userStore.isAdmin());

  // change tracking
  private originalRows = new Map<string, string>();

  // save
  showSaveConfirm = signal(false);
  saving = signal(false);
  saveError = signal<string | null>(null);

  // discard
  showDiscardConfirm = signal(false);

  // unsaved-changes leave guard
  showLeaveConfirm = signal(false);
  private leaveResolve: ((allow: boolean) => void) | null = null;

  constructor(
    private route: ActivatedRoute,
    private organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private surveyModelService: ApiSurveyModelService,
    private cmdMappingService: ApiSurveyCmdMappingService,
    private injector: Injector) {}

  ngOnInit() {
    // surveySelectedGuard on the route guarantees these are present and valid.
    const organizationId = this.organizationStore.selectedOrganizationId()!;
    const surveyId = this.route.snapshot.paramMap.get('id')!;

    this.loadSurveyModel(organizationId, surveyId);
  }

  private loadSurveyModel(organizationId: string, surveyId: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.surveyModelService
      .getSurveyModel(organizationId, surveyId)
      .subscribe({
        next: (survey) => {
          this.survey = survey;
          this.rows = this.buildRows(this.sortByCsvPosition(survey.cmd));
          this.takeSnapshot();
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Failed to load survey model.');
          this.loading.set(false);
        },
      });
  }

  private sortByCsvPosition(cmd: CmdModel[]): CmdModel[] {
    return [...cmd].sort((a, b) => {
      if (a.csvPosition === null && b.csvPosition === null) return 0;
      if (a.csvPosition === null) return 1;
      if (b.csvPosition === null) return -1;
      return a.csvPosition - b.csvPosition;
    });
  }

  private buildRows(cmd: CmdModel[]): EditableCmdRow[] {
    return cmd.map(model => ({
      model: { ...model },
      originalKey: { csvPosition: model.csvPosition, dbColumn: model.dbColumn },
      markedForDelete: false,
      translationText: model.columnValueTranslation ? JSON.stringify(model.columnValueTranslation) : '',
      translationError: null
    }));
  }

  setActiveTab(tab: SurveyCMDTab): void {
    this.activeTab.set(tab);
  }

  // ── Row editing ──────────────────────────────────────────────────────────

  addRow(): void {
    if (!this.canEdit()) return;
    this.rows.push({
      model: defaultCmdModel(),
      originalKey: null,
      markedForDelete: false,
      translationText: '',
      translationError: null
    });

    // The new row's input only exists after the next render — focusing it also scrolls it into view.
    afterNextRender(() => this.dbColumnInputs.last?.nativeElement.focus(), { injector: this.injector });
  }

  toggleDelete(row: EditableCmdRow): void {
    if (!this.canEdit()) return;
    if (!row.originalKey) {
      // Never saved — just drop it, nothing to tell the backend about.
      this.rows = this.rows.filter(r => r !== row);
      return;
    }
    row.markedForDelete = !row.markedForDelete;
  }

  isObject(value: unknown): boolean {
    return typeof value === 'object' && value !== null;
  }

  onTranslationTextChange(row: EditableCmdRow, text: string): void {
    if (!this.canEdit()) return;
    row.translationText = text;
    const trimmed = text.trim();
    if (!trimmed) {
      row.model.columnValueTranslation = null;
      row.translationError = null;
      return;
    }
    try {
      row.model.columnValueTranslation = JSON.parse(trimmed);
      row.translationError = null;
    } catch {
      row.translationError = 'Invalid JSON';
    }
  }

  // ── Change tracking ──────────────────────────────────────────────────────

  private takeSnapshot(): void {
    this.originalRows.clear();
    for (const row of this.rows) {
      if (row.originalKey) {
        this.originalRows.set(cmdKeyString(row.originalKey), JSON.stringify(row.model));
      }
    }
  }

  getRowChangeStatus(row: EditableCmdRow): RowChangeStatus {
    if (row.markedForDelete) return 'deleted';
    if (!row.originalKey) return 'new';
    const original = this.originalRows.get(cmdKeyString(row.originalKey));
    return JSON.stringify(row.model) === original ? 'clean' : 'modified';
  }

  get pendingChangesCount(): number {
    return this.rows.filter(r => this.getRowChangeStatus(r) !== 'clean').length;
  }

  get newRowsCount(): number {
    return this.rows.filter(r => this.getRowChangeStatus(r) === 'new').length;
  }

  get modifiedRowsCount(): number {
    return this.rows.filter(r => this.getRowChangeStatus(r) === 'modified').length;
  }

  get deletedRowsCount(): number {
    return this.rows.filter(r => this.getRowChangeStatus(r) === 'deleted').length;
  }

  // ── Validation ───────────────────────────────────────────────────────────

  // The same dbColumn may appear at different csvPositions — only a repeated pair is a conflict.
  get duplicateKeys(): Set<string> {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const row of this.rows) {
      if (row.markedForDelete || !row.model.dbColumn.trim()) continue;
      const key = cmdKeyString(row.model);
      if (seen.has(key)) dupes.add(key);
      else seen.add(key);
    }
    return dupes;
  }

  // Lists the clashing rows by their displayed row number (# column), e.g. "#2 & #5, #3 & #7".
  get duplicateKeysText(): string {
    const groups = new Map<string, number[]>();
    this.rows.forEach((row, index) => {
      if (!this.isDuplicateKey(row)) return;
      const key = cmdKeyString(row.model);
      groups.set(key, [...(groups.get(key) ?? []), index + 1]);
    });
    return Array.from(groups.values())
      .map(rowNumbers => rowNumbers.map(n => `#${n}`).join(' & '))
      .join(', ');
  }

  isDuplicateKey(row: EditableCmdRow): boolean {
    return !row.markedForDelete && !!row.model.dbColumn.trim() && this.duplicateKeys.has(cmdKeyString(row.model));
  }

  get hasEmptyDbColumn(): boolean {
    return this.rows.some(r => !r.markedForDelete && !r.model.dbColumn.trim());
  }

  get hasInvalidJson(): boolean {
    return this.rows.some(r => !r.markedForDelete && !!r.translationError);
  }

  get hasValidationErrors(): boolean {
    return this.duplicateKeys.size > 0 || this.hasEmptyDbColumn || this.hasInvalidJson;
  }

  // ── Save ─────────────────────────────────────────────────────────────────

  requestSave(): void {
    if (!this.canEdit() || this.hasValidationErrors) return;
    this.saveError.set(null);
    this.showSaveConfirm.set(true);
  }

  confirmSave(): void {
    if (!this.canEdit()) return;
    const organizationId = this.organizationStore.selectedOrganizationId();
    const surveyId = this.survey?.id;
    if (!organizationId || !surveyId) return;

    const toDelete: CmdKey[] = [];
    const createOrUpdate: ApiCmdMappingModel[] = [];

    for (const row of this.rows) {
      const status = this.getRowChangeStatus(row);
      if (status === 'clean') continue;

      if (status === 'deleted') {
        if (row.originalKey) toDelete.push({ ...row.originalKey });
        continue;
      }

      // oldCsvPosition/oldDbColumn tell the backend which existing record an edit replaces
      // (even if its key changed); null marks a brand-new record.
      createOrUpdate.push({
        ...row.model,
        oldCsvPosition: row.originalKey?.csvPosition ?? null,
        oldDbColumn: row.originalKey?.dbColumn ?? null
      });
    }

    const request: CmdMappingRequestModel = {
      delete: toDelete,
      createOrUpdate
    };

    if (request.delete.length === 0 && request.createOrUpdate.length === 0) {
      this.showSaveConfirm.set(false);
      return;
    }

    this.saving.set(true);
    this.cmdMappingService.updateSurveyCmdMapping(organizationId, surveyId, request).subscribe({
      next: (response) => {
        this.saving.set(false);
        if (!response.isFullSuccess) {
          this.saveError.set(response.failed.map(f => `${f.dbColumn}: ${f.reason}`).join(', ') || 'Some changes failed to save.');
          return;
        }
        this.showSaveConfirm.set(false);
        this.loadSurveyModel(organizationId, surveyId);
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(this.formatApiError(err, 'Failed to save. Please try again.'));
      }
    });
  }

  private formatApiError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const apiError = err.error as ApiErrorResponse<ApiSurveyCmdMappingErrorCode> | undefined;
      if (apiError?.code && apiError?.description) {
        const details = apiError.details?.length
          ? ` (${apiError.details.map(d => typeof d === 'string' ? d : JSON.stringify(d)).join('; ')})`
          : '';
        return `[${apiError.code}] ${apiError.description}${details}`;
      }
      return `${fallback} | ${err.message}`;
    }
    return `${fallback} | ${err}`;
  }

  cancelSave(): void {
    if (this.saving()) return;
    this.saveError.set(null);
    this.showSaveConfirm.set(false);
  }

  // ── Discard ──────────────────────────────────────────────────────────────

  requestDiscard(): void {
    if (!this.canEdit()) return;
    this.showDiscardConfirm.set(true);
  }

  confirmDiscard(): void {
    if (!this.canEdit()) return;
    const models = Array.from(this.originalRows.values()).map(json => JSON.parse(json) as CmdModel);
    this.rows = this.buildRows(this.sortByCsvPosition(models));
    this.showDiscardConfirm.set(false);
  }

  cancelDiscard(): void {
    this.showDiscardConfirm.set(false);
  }

  // ── Leave guard ──────────────────────────────────────────────────────────

  setLeaveResolve(fn: (allow: boolean) => void): void {
    this.leaveResolve = fn;
  }

  confirmLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(true);
    this.leaveResolve = null;
  }

  cancelLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(false);
    this.leaveResolve = null;
  }
}
